// type for Emission Factor Database entries
export interface InterventionInput {
  _id: string;
  input_code: string;
  input_name: string;
  input_type: InputType;
  emission_factor: number;
  emission_factor_unit: string;
  input_source: string;
  year: string;
  region: string;
  version?: string;
  status: string;
  [key: string]: any;
}

export enum UserGroup {
  PROJECT_DEVELOPER = "Project Developer",
  PROBA_OPERATOR = "Proba Operator",
  VALIDATOR = "Validator",
  VERIFIER = "Verifier",
  ADMIN = "Admin",
  ORG_ADMIN = "Org Admin",
  USER = "User"
}

export type MethodologyRequirementType =
  | "string"
  | "number"
  | "boolean"
  | "select";

export interface Option {
  value: string;
  label: string;
}

export interface MethodologyVariable {
  id: string;
  name: string;
  description?: string;
  display_name?: string;
  type: MethodologyRequirementType;
  required: boolean;
  options?: Option[];
  default?: any;
  domain?: string;
  url?: string;
  metadata?: Record<string, any>;
  dynamic?: boolean;
  level?: string;
  conditionalOn?: { requirementId: string; value: any };
  notes?: string;  // Additional guidance notes
}

export interface MethodologyConstant {
  id: string;
  name: string;
  description?: string;
  display_name?: string;
  value: number;
}

export interface MethodologyEquation {
  id: string;
  name: string;
  description?: string;
  expression: string;
  group_by?: string[];
  aggregation?: string;
  input_var?: string;
  notes?: string;  // Added for equation-specific guidance
}

export interface BaselineApproach {
  name: string;
  description?: string;
  baseline_input_type: InputType;
  level: 'asset' | 'region';  
  yield_periods_required: number;
  update_frequency: number;
  metadata?: Record<string, any>;
}

export interface Methodology {
  id: string;
  name: string;
  description: string;
  domain: string;
  url?: string;
  variables?: MethodologyVariable[];
  constants?: MethodologyConstant[];

  equations: MethodologyEquation[];
  metadata?: Record<string, any>;
  version?: string;
  status?: string;

  asset_type?: AssetType;
  project_input_type?: InputType;
  yield_period?: number;
  yield_period_unit?: string;
  baseline_approaches: BaselineApproach[];
}

export interface ProjectRegion {
  name: string;
  code: string;
}

export interface Project {
  _id: string;
  name: string;
  project_id: string;
  type: string;
  region: ProjectRegion;
  organization: string;
  status: string;
  methodologies: string[];
  standards_used?: string;
  ghg_project_summary: string;
  crediting_period?: string[];
  project_period?: string[];
  buffer_pool_value?: number;
  is_pre_credits_active?: boolean;
  pre_credits?: number;
  sponsor_pre_credits_flag?: boolean;
  creator?: string;
  updator?: string;
  created?: string;
  updated?: string;
  param_id?: string;
  assignment?: any[];
  is_updated_to_blockchain?: boolean;
  project_uid: string;
}

// Intervention related types
export interface Intervention {
  intervention_id?: string;  // Primary key
  project_id: string;  // Reference to parent project
  name: string;
  description: string;
  methodology_id: string;  // Reference to methodology
  status: InterventionStatus;
  assets?: Asset[];
  created?: string;
  updated?: string;
  creator?: string;
  updator?: string;
  version?: number;
}

export enum InterventionStatus {
  DRAFT = 'draft',
  PENDING_REVIEW = 'pending_review',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export interface CustomerAsset {
  customer_asset_id: string;
  asset_name: string;
  plot_size: number;
  plot_size_unit: string;
  customer_soil_code: string;
  periods: Period[];
}

export interface CustomerInputData {
  customer_input_code: string;
  customer_inhibitor_code: string;
  methodology_id: string;
  inhibitor_rate: number;
  rate: number;
  unit: string;
}

export interface PeriodValues {
  customer_crop_code?: string;
  crop_yield?: number;
  crop_yield_unit?: string;
  baseline_inputs: CustomerInputData[];
  project_inputs: CustomerInputData[];
};

export interface Period {
  period_year: number;
  period_sequence: number;
  estimated: PeriodValues;
  realized: PeriodValues;
};


// API response type remains the same
export interface InterventionResponse {
  success: boolean;
  data: Intervention;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface Asset {
  _id?: string;
  external_asset_id: string;
  name: string;
  description?: string;
  type?: AssetType;
  intervention_ids: string[]; 
  param_id?: string;
  status?: 'active' | 'inactive' | 'pending';
  output?: OutputData[];
  created?: string;
  updated?: string;
  creator?: string;
  updator?: string;
}

export interface LmuAsset extends Asset {
  type: "land_management_unit";
  area: number;
  plot_size?: number;
  plot_size_unit?: string;
  soil?: string;
}

export interface Crop extends OutputData {
  crop_id: string;
  external_crop_id: string;
  crop_type: string;
  crop_yield: number;
  crop_yield_unit: string;
  yield_period: string;
}


// Add this for API responses
export interface AssetResponse<T extends AssetType> {
  success: boolean;
  data: {
    data: T extends keyof AssetTypeMap ? AssetTypeMap[T][] : Asset[];
    total?: number;
  };
  message?: string;
  errors?: Record<string, string[]>;
}


export interface InputData {
  code: string
  name: string;
  type: InputType;                // e.g., "fertilizer", "stabilizer", "fuel", etc.
  external_asset_id: string;
  is_realized?: boolean;
  product_name: string;           // Human-readable label or reference
  application_round?: number;     // Optional sequencing within yield period
  yield_period: string;           // Time window (e.g. year, season)
  emission_data?: Record<string, any>; // Optional emission calculations and methodology data
};

// Fertilizer-specific parent type
export interface FertilizerInput extends InputData {
  application_rate?: number;   // fertilizer application rate Kg N / ha
};

// Controlled-Release Fertilizer extension
export interface ControlledReleaseFertilizerInput extends FertilizerInput {
  type: 'controlled_release_fertilizer'
  crf_blend_percent?: number;
  crf_application_rate?: number;
};

// Nitrogen Stabilizer Fertilizer extension
export interface StabilizedFertilizerInput extends FertilizerInput {
  type: 'nitrogen_stabilized_fertilizer'
  stabilizer_type: string;
  stabilizer_application_rate?: number;
};

export interface FuelInput extends FertilizerInput {
  type: 'fuel';
  fuel_type: string;
}

export const inputTypeMap = {
  conventional_fertilizer: {} as FertilizerInput,
  nitrogen_stabilized_fertilizer: {} as StabilizedFertilizerInput,
  controlled_release_fertilizer: {} as ControlledReleaseFertilizerInput,
  low_carbon_fertilizer: {} as FertilizerInput,
  fuel: {} as FuelInput,
  other: {} as InputData,
};

export type InputTypeMap = typeof inputTypeMap;
export type InputType = keyof InputTypeMap;

export type AssetTypeMap = {
  land_management_unit: LmuAsset;
  fertilizer_production_facility: any;
  generic: any;
}

export type AssetType = keyof AssetTypeMap;

export type OutputTypeMap = {
  crop: Crop;
}

export type OutputType = keyof OutputTypeMap;

export interface CustomerCodes {
  input_code: string;
  inhibitor?: string;
  crop?: string;
  soil?: string;
}

export interface DataColumn {
  title: string;
  dataIndex: keyof InputData | keyof OutputData;
  required?: boolean;
  type: 'string' | 'number' | 'date';
}

export interface OutputData {
  asset_id: string;
  external_asset_id: string;
  period: string;
  [key: string]: any;
}

